import type {
    PushRequest,
    WireEntity,
    WireNotice,
    WireOperationBody,
    WireProblem,
    WireScope,
} from './wire';

/**
 * De records in de database van één omgeving (`TenantDatabase`). Ze volgen het
 * draadformaat: een antwoord heeft een `questionId` plus een `multiScope`, geen
 * samengestelde string. Alleen de opslag zelf gebruikt een `slot` - een
 * lokale indexsleutel die nooit over de draad gaat en die de engine nooit ziet.
 */

/**
 * Een templateversie zoals `/sync/bootstrap` hem aankondigt, plus wat er van
 * het pakket lokaal staat. Het document zelf staat apart in
 * {@link PackageDocumentRecord}: een lijst van versies (Mijn dag, opruimen)
 * hoeft dan geen 4,6 MB per versie te lezen.
 */
export interface TemplateVersionRecord {
    uuid: string;
    version: number;
    /** De checksum die de server aankondigde. */
    checksum: string;
    packageUrl: string;
    /** `"<checksum>"` van het document dat lokaal staat; gaat mee als `If-None-Match`. `null` = nog niet opgehaald. */
    etag: string | null;
    byteSize: number;
    fetchedAt: number | null;
}

/**
 * Het document van `GET /template-versions/{uuid}/package`, integraal en
 * ongewijzigd weggeschreven. `entities/template` valideert het en vertaalt het
 * naar het enginepakket.
 */
export interface PackageDocumentRecord {
    uuid: string;
    document: unknown;
}

export interface OrderAddress {
    street: string | null;
    houseNumber: string | null;
    houseNumberSuffix: string | null;
    postcode: string | null;
    city: string | null;
    /** Het bouwjaar uit de BAG. Ontbreekt op een opdracht van vóór dit veld. */
    bagBuildYear?: number | null;
}

export interface OrderRecord {
    uuid: string;
    number: string;
    status: string;
    scheduledAt: string | null;
    scheduledEndAt: string | null;
    serviceCode: string | null;
    serviceLabel: string | null;
    address: OrderAddress | null;
    clientName: string | null;
    clientPhone: string | null;
    clientEmail: string | null;
    inspectionUuid: string | null;
    /**
     * De persoon aan wie de opdracht is toegewezen, `null` als niemand. Ontbreekt
     * op een opdracht die is opgeslagen voordat de server dit meestuurde; de
     * volgende bootstrap vult het aan.
     */
    assigneeUuid?: string | null;
}

export interface InspectionRecord {
    uuid: string;
    orderUuid: string | null;
    /** `not_started` · `in_progress` · `completed` · `submitted` */
    status: string;
    /** `null` als er voor deze dienst geen vragenlijst is gepubliceerd. */
    templateVersionUuid: string | null;
    startedAt: string | null;
    completedAt: string | null;
    notes: string | null;
    /**
     * Lokale concepten zonder draadvorm, zoals het NTA 8800-formulier. Ze gaan
     * niet over de lijn zolang het contract er geen entiteit voor heeft.
     */
    drafts: Record<string, unknown>;
    clientUpdatedAt: string | null;
    /** Moment van indienen, zoals `POST /inspections/{uuid}/submit` het teruggaf. */
    submittedAt?: string | null;
}

export interface AnswerRecord {
    answerUuid: string;
    inspectionUuid: string;
    questionId: number;
    multiScope: WireScope;
    /** Lokale indexsleutel voor (vraag, bereik); zie `answerSlot()`. */
    slot: string;
    optionIds: number[] | null;
    valueNumber: number | null;
    valueText: string | null;
    reportText: string | null;
    /** Waar het gebrek zit, in een handvol woorden; `null` als er niets staat. */
    location: string | null;
    directCost: string | null;
    termCost: string | null;
    improvementCost: string | null;
    costGroupId: number | null;
    urgency: string | null;
    nhgClassification: string | null;
    clientUpdatedAt: string | null;
    /**
     * De server kent dit antwoord: het kwam uit bootstrap of pull, of een push
     * gaf `applied`/`unchanged`. Pas dan mag een foto eraan gekoppeld worden.
     */
    confirmed: 0 | 1;
}

export interface MultiCountRecord {
    inspectionUuid: string;
    /** Lokale indexsleutel voor (container, bereik); zie `multiCountSlot()`. */
    slot: string;
    containerNodeId: number;
    multiScope: WireScope;
    count: number;
    clientUpdatedAt: string | null;
}

export interface FindingRecord {
    findingUuid: string;
    inspectionUuid: string;
    /**
     * Het onderdeel waar een handmatige bevinding bij hoort, in draadvorm (`node_id`
     * plus `multi_scope`). Ontbreekt bij bevindingen van vóór dit veld: dan geen onderdeel.
     */
    nodeId?: number | null;
    multiScope?: WireScope;
    title: string | null;
    description: string | null;
    /** Waar het gebrek zit, in een handvol woorden; `null` als er niets staat. */
    location?: string | null;
    severity: string | null;
    urgency: string | null;
    directCost: string | null;
    termCost: string | null;
    improvementCost: string | null;
    costGroupId: number | null;
    nhgClassification: string | null;
    /** `answer` · `manual` · `suggestion`; alleen `manual` is vanuit de app te wijzigen. */
    source: string;
    clientUpdatedAt: string | null;
    /** De server kent deze bevinding (uit bootstrap of pull, of een push gaf `applied`/`unchanged`). */
    confirmed?: 0 | 1;
}

/**
 * - `pending`: wacht tot het antwoord op de server staat, of tot het netwerk er is;
 * - `uploading`: het verzoek loopt;
 * - `uploaded`: de server heeft hem (eventueel met `urls`);
 * - `failed`: de server wees het bestand af - staat in de conflictenlijst.
 */
export type PhotoState = 'pending' | 'uploading' | 'uploaded' | 'failed';

export interface PhotoRecord {
    /** Op het apparaat gemaakt vóór de eerste poging; de idempotency key van `POST /media`. */
    clientUuid: string;
    inspectionUuid: string;
    /**
     * De vraag waar de foto bij hoort. Het antwoord-uuid wordt pas bij de upload
     * opgezocht. Bij een foto van een handmatige bevinding `0` met een leeg bereik:
     * die hoort bij `findingUuid`.
     */
    questionId: number;
    multiScope: WireScope;
    /** `answerSlot()` van de vraag, of `findingPhotoSlot()` van de bevinding. */
    slot: string;
    /** De handmatige bevinding waar de foto bij hoort; ontbreekt of `null` bij een foto van een vraag. */
    findingUuid?: string | null;
    /** Het `answer_uuid` waarmee de foto is geüpload, of dat de server meegaf. */
    answerUuid: string | null;
    /** De bytes; `null` voor een foto die alleen op de server staat. */
    blob: Blob | null;
    mimeType: string;
    fileName: string;
    byteSize: number;
    caption: string | null;
    takenAt: string;
    state: PhotoState;
    attempts: number;
    nextAttemptAt: number;
    lastError: string | null;
    urls: { thumb: string | null; web: string | null } | null;
    createdAt: number;
    /** De `uuid` van de mediaresource: na een upload, of uit bootstrap en pull. Nodig om hem te verwijderen. */
    mediaUuid?: string | null;
    /**
     * `1`: de inspecteur verwijderde de foto. Hij is niet meer te zien en de
     * sync-engine verwijdert hem op de server (`DELETE /media/{uuid}`).
     */
    deleted?: 0 | 1;
}

export type OutboxStatus = 'pending' | 'inFlight';

/**
 * Eén bewerking in de outbox, in de vorm van het draadformaat plus de
 * boekhouding van de queue.
 *
 * - `opUuid` is de ULID van de bewerking (`op_uuid`).
 * - `seq` (auto-increment) bepaalt de volgorde; de server past in volgorde toe.
 * - `entityKey` is de natuurlijke sleutel binnen de omgeving ("dit antwoord",
 *   "dit aantal"). Een nog niet verstuurde bewerking op dezelfde sleutel wordt
 *   vervangen in plaats van er een tweede achter te zetten.
 * - `batchUuid` is gezet zodra de bewerking in een verstuurde batch zit. Die
 *   batch gaat bij een netwerkfout **ongewijzigd** opnieuw, met dezelfde
 *   `batch_uuid`: de server antwoordt dan exact hetzelfde.
 */
export type OutboxOperation = WireOperationBody & {
    seq?: number;
    opUuid: string;
    clientUpdatedAt: string;
    inspectionUuid: string | null;
    entityKey: string;
    status: OutboxStatus;
    batchUuid: string | null;
    createdAt: number;
};

/** Een verstuurde batch. Onveranderlijk tot er een antwoord is. */
export interface BatchRecord {
    batchUuid: string;
    opSeqs: number[];
    /** De request-body zoals hij de eerste keer verstuurd is. */
    body: PushRequest;
    attempts: number;
    nextAttemptAt: number;
    createdAt: number;
    lastError: string | null;
}

/**
 * - `conflict`: de server had een nieuwere versie; die staat nu lokaal.
 * - `rejected`: de server wees de bewerking af; het `problem` zegt waarom.
 * - `photo`: de server wees een foto af (type, grootte, inhoud).
 * - `notice`: de server nam de bewerking over, maar niet precies waar de inspecteur
 *   haar zette - de vragenlijst werd intussen vernieuwd. Geen fout: de `notice` zegt
 *   waar het nu staat, en de sync-indicator blijft rustig.
 */
export type ConflictKind = 'conflict' | 'rejected' | 'photo' | 'notice';

export interface ConflictRecord {
    id: string;
    kind: ConflictKind;
    entity: WireEntity | 'media';
    entityKey: string;
    inspectionUuid: string | null;
    /** Wat het apparaat wilde versturen. */
    localPayload: unknown;
    /** Wat de server teruggaf (`server_state`), als hij dat deed. */
    serverState: unknown;
    problem: WireProblem | null;
    /** Alleen bij `notice`: wat de server erover zei. */
    notice?: WireNotice | null;
    clientUpdatedAt: string | null;
    detectedAt: number;
    /** 0 zolang de inspecteur de melding niet gezien heeft. */
    acknowledged: 0 | 1;
}

export interface SyncMetaRecord {
    key: string;
    value: unknown;
}

export const SYNC_META_KEYS = {
    cursor: 'cursor',
    bootstrappedAt: 'bootstrapped-at',
    lastSyncedAt: 'last-synced-at',
    user: 'user',
    /**
     * Telt elke keer dat bewerkingen de outbox verlaten (bevestigd of geweigerd).
     * Een pull of bootstrap die onderweg was toen dat gebeurde, kan ouder zijn dan
     * wat hier nu staat - bijvoorbeeld uit een tweede tabblad - en wordt dan opnieuw
     * opgehaald in plaats van toegepast.
     */
    settled: 'settled-generation',
} as const;

/* ------------------------------------------------------------------ */
/* Lokale indexsleutels                                                */
/* ------------------------------------------------------------------ */

/** `"178.1/156.0"` - alleen voor indexen in deze database, nooit over de draad. */
export function scopeSignature(scope: WireScope): string {
    return scope.map((entry) => `${entry.container_node_id}.${entry.index}`).join('/');
}

/** De opslagsleutel van (vraag, bereik) binnen één opname. */
export function answerSlot(questionId: number, scope: WireScope): string {
    return `q${questionId}|${scopeSignature(scope)}`;
}

/** De opslagsleutel van de foto's van één handmatige bevinding. */
export function findingPhotoSlot(findingUuid: string): string {
    return `f|${findingUuid}`;
}

/** De opslagsleutel van (container, bereik) binnen één opname. */
export function multiCountSlot(containerNodeId: number, scope: WireScope): string {
    return `m${containerNodeId}|${scopeSignature(scope)}`;
}

export function sameScope(a: WireScope, b: WireScope): boolean {
    return scopeSignature(a) === scopeSignature(b);
}
